import { useState } from "react";
import { Check, Copy, Mail, Pencil, Phone, Trash2 } from "lucide-react";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { TableCell, TableRow } from "@/components/ui/table";
import { SEMINAR } from "@/seminars";

function whatsappHref(link, url) {
  const first = link.advisor_name.split(" ")[0];
  const message = `Hi ${first}, here is your unique ${SEMINAR.admin.tag} webinar referral link (${link.code}):\n${url}\n\nShare it with your clients. You'll receive an email each time someone registers through it.`;
  return `https://wa.me/?text=${encodeURIComponent(message)}`;
}

export function formatMobile(digits) {
  if (!digits) return "";
  if (digits.startsWith("65") && digits.length === 10) return `+65 ${digits.slice(2, 6)} ${digits.slice(6)}`;
  return `+${digits}`;
}

function InlineField({ code, field, value, type, icon: Icon, placeholder, empty, title, format = (v) => v, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const save = () => {
    setEditing(false);
    if (draft.trim() !== (value || "")) onSave(draft.trim());
  };
  if (editing) {
    return (
      <Input
        autoFocus
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setDraft(value || ""); setEditing(false); } }}
        placeholder={placeholder}
        className="mt-1 h-8 w-64 rounded-lg border-white/15 bg-white/5 text-xs"
        data-testid={`link-${field}-input-${code}`}
      />
    );
  }
  return (
    <button type="button" onClick={() => { setDraft(value || ""); setEditing(true); }} className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-white/55 transition-colors hover:text-white" title={title} data-testid={`link-${field}-${code}`}>
      <Icon className="h-3 w-3" /> {value ? format(value) : <span className="italic text-amber-300/80">{empty}</span>} <Pencil className="h-2.5 w-2.5 opacity-50" />
    </button>
  );
}

export function LinkRow({ link, url, copied, onCopy, onUpdate, onDelete }) {
  return (
    <TableRow className="border-white/8 hover:bg-white/5" data-testid={`link-row-${link.code}`}>
      <TableCell className="font-bold text-[#f2d28f]" data-testid={`link-code-${link.code}`}>{link.code}</TableCell>
      <TableCell>
        <div className="text-sm font-semibold" data-testid={`link-name-${link.code}`}>{link.advisor_name}</div>
        <div className="flex flex-col items-start">
          <InlineField code={link.code} field="email" type="email" value={link.email} icon={Mail} placeholder="adviser@infinityfa.com.sg" empty="No email · adviser not notified" title="Edit notification email" onSave={(email) => onUpdate({ email })} />
          <InlineField code={link.code} field="mobile" type="tel" value={link.mobile} icon={Phone} placeholder="+65 9123 4567" empty="No mobile · book-a-chat uses group WhatsApp" title="Edit WhatsApp mobile (used by the follow-up 'Book a chat' button)" format={formatMobile} onSave={(mobile) => onUpdate({ mobile })} />
        </div>
        {link.label && <div className="text-[11px] text-white/40">{link.label}</div>}
      </TableCell>
      <TableCell className="max-w-[260px] truncate text-xs text-white/60" title={url} data-testid={`link-url-${link.code}`}>{url}</TableCell>
      <TableCell className="text-right text-lg font-bold" data-testid={`link-count-${link.code}`}>{link.registrations}</TableCell>
      <TableCell>
        <Switch checked={link.active} onCheckedChange={(active) => onUpdate({ active })} className="data-[state=checked]:bg-[#d9ad57]" data-testid={`toggle-link-btn-${link.code}`} />
        {!link.active && <Badge variant="outline" className="ml-2 border-white/15 text-[10px] text-white/50">paused</Badge>}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="outline" className="h-8 rounded-full border-white/15 bg-white/5 px-3 text-xs font-semibold hover:bg-[#f2d28f] hover:text-[#07130f]" onClick={onCopy} data-testid={`copy-link-btn-${link.code}`}>
            {copied ? <Check className="mr-1 h-3.5 w-3.5 text-emerald-300" /> : <Copy className="mr-1 h-3.5 w-3.5" />} {copied ? "Copied" : "Copy link"}
          </Button>
          <Button asChild size="sm" variant="outline" className="h-8 rounded-full border-[#25d366]/40 bg-[#25d366]/10 px-3 text-xs font-semibold text-[#5ee39a] hover:bg-[#25d366] hover:text-[#07130f]">
            <a href={whatsappHref(link, url)} target="_blank" rel="noopener noreferrer" title="Send this link to the adviser on WhatsApp" data-testid={`whatsapp-link-btn-${link.code}`}>
              <WhatsAppIcon size={14} className="mr-1" /> WhatsApp
            </a>
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-300/70 hover:text-red-200" title="Delete link" onClick={onDelete} data-testid={`delete-link-btn-${link.code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
